import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import {
  AuctionExtraData,
  AutoLotView,
  BoundaryStatus,
  getCalcData,
  getLotSourceFromParams,
  hasManualTranslations,
  hasTranslations,
  isManager,
  mergeSavedLotToAutoLotView,
  ReactiveLotData,
} from 'src/app/const';
import {
  AlertTargetEnum,
  Lot,
  LotTypeEnum,
  UserLotGQL,
  UserLotQuery,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import {
  AutoLotQuery,
  LotSource,
} from 'src/app/modules/graphql/service/graphql-cars-default-service';
import { AuctionVideoWatchService } from 'src/app/services/auction-video-watch/auction-video-watch.service';
import { LotWatchRequestInspectionData } from 'src/app/services/auction-video-watch/types/lot-watch-request-inspection-data.type';
import { AuctionService } from 'src/app/services/auction.service';
import { SanctionsService } from 'src/app/services/sanctions.service';

import { environment } from '../../../../../environments/environment';
import { LimitsService } from '../../../../services/limits.service';
import { LotViewsService } from '../../../../services/lot-views.service';
import { pickCurrentTradingTimeShift } from '../../../../utils/pick-current-trading-time-shift.util';
import { AutoLotGuideService } from '../../../guides/auto-lot-guide/services/auto-lot-guide.service';
import { DisableMediaSavingService } from 'src/app/services/security/disable-media-saving.service';
import { normalizeFrame } from 'src/app/utils/frame.util';

@Component({
  selector: 'app-auto-lot',
  templateUrl: './auto-lot.component.html',
  styleUrls: ['./auto-lot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotComponent implements OnInit, OnDestroy {
  data$: ReactiveLotData = of({});
  loading$ = new BehaviorSubject<boolean>(true);
  lotNotFound$ = of(false);
  refetch$ = new Subject<void>();

  auctionData$: Observable<AuctionExtraData>;
  inspectionLoading$: Observable<boolean>;
  inspectionData$: Observable<LotWatchRequestInspectionData>;

  activeSlide = 1;

  LotSource = LotSource;
  BoundaryStatus = BoundaryStatus;

  statisticsOpen = false;

  AlertTargetEnum = AlertTargetEnum;

  isGuideLotIconsVisible = false;

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private limitsService: LimitsService,
    private userLotGQL: UserLotGQL,
    private sanctionsService: SanctionsService,
    private lotViewsService: LotViewsService,
    private autoLotGuideService: AutoLotGuideService,
    private auctionService: AuctionService,
    private disableMediaSavingService: DisableMediaSavingService,
    private auctionVideoWatchService: AuctionVideoWatchService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.data$ = combineLatest([
      this.route.params,
      this.refetch$.asObservable().pipe(startWith(true)),
    ]).pipe(
      tap(() => this.loading$.next(true)),
      map(([params]) => ({
        lotId: params.lot,
        lotType: LotTypeEnum.Auto,
        lotSource: getLotSourceFromParams(params),
      })),
      switchMap((initData) =>
        this.userLotGQL
          .fetch(
            {
              id: initData.lotId,
              lotSource: initData.lotSource,
              subgroup: initData.lotType,
              siteDomain: environment.lotSiteDomain,
            },
            { fetchPolicy: 'network-only' },
          )
          .pipe(map((userResult) => ({ initData, userResult }))),
      ),
      tap(({ initData }) =>
        this.lotViewsService.increment(initData.lotId, initData.lotType, initData.lotSource),
      ),
      switchMap((data) =>
        iif(
          () => Boolean(data.userResult.data.lot),
          of(data).pipe(
            map((data) => ({
              ...data,
              lot: this.compileAutoLotView(
                'const',
                null,
                data.userResult.data.lot,
                data.userResult.data.lotViewsCount,
              ),
            })),
            map((data) => ({
              ...data.initData,
              lot: data.lot,
              savedLot: data.userResult.data?.lot,
              bids: data.userResult.data?.lot?.bids ?? [],
              currentUser: data.userResult.data.currentUser,
              icons: data.userResult.data?.lot?.icons ?? [],
              currentUserLastBid: data.userResult.data?.lot?.currentUserLastBid,
              currentUserLimitsOverrideAvailable:
                data.userResult.data?.lot?.currentUserLimitsOverrideAvailable,
              currentUserLotNote: data.lot.currentUserLotNote,
              bestBid: data.userResult.data?.lot?.bestBid,
              hasBestBid: data.userResult.data?.lot?.hasBestBid,
              currentUserLotMarks: data.userResult.data?.lot?.currentUserLotMarks || [],
              translations: data.userResult.data?.lot?.translations,
              fullVin: Boolean(data.userResult.data?.lot?.vin),
              isUsingSavedImages: Boolean(data.userResult.data.lot),
              limits: this.limitsService.getLimits(data.lot, data.userResult.data?.lot as Lot),
              isSanctioned: this.sanctionsService.isLotSanctioned(
                data.lot,
                data.userResult.data.currentUser,
              ),
              hasTranslations: hasTranslations(data.userResult.data?.lot?.translations),
              hasManualTranslations: hasManualTranslations(data.userResult.data?.lot?.translations),
              isManager: isManager(data.userResult.data?.currentUser),
              calcData: getCalcData(
                data.lot,
                LotTypeEnum.Auto,
                data.userResult.data?.lot?.currentUserLastBid?.amount,
              ),
            })),
            tap((data) => {
              const { bid, auctionName, company, modelName, frame } = data.lot;
              const normalizedFrame = normalizeFrame(frame);
              this.title.setTitle(
                `${bid} - ${auctionName} - ${company} ${modelName} ${normalizedFrame} | ECar JP`,
              );
              if (
                data.lot?.auctionName?.toLowerCase()?.includes('uss') &&
                data.currentUser?.canDownloadUssPhotos === false
              ) {
                this.disableMediaSavingService.resume();
              }
            }),
          ),
          of({ ...data.initData }),
        ),
      ),
      tap(() => this.loading$.next(false)),
      shareReplay(1),
    );

    this.fetchInspection();
    this.fetchAuctionData();

    this.lotNotFound$ = combineLatest([this.loading$, this.data$]).pipe(
      map(([loading, data]) => !loading && !data.lot),
    );
  }

  private compileAutoLotView(
    dumpVersion: string,
    autoLot?: AutoLotQuery['autoLot'],
    savedLot?: UserLotQuery['lot'],
    viewsCount?: UserLotQuery['lotViewsCount'],
  ): AutoLotView {
    let lot: AutoLotView;

    if (autoLot) {
      lot = {
        ...autoLot,
        images: autoLot.images.split('#').filter((url) => !!url),
        // TODO: возможно временно убрано по просьбе Vitalii Kubar
        // .map((url) => url.replace('https://p3.aleado.com/', environment.imageProxy)),
        auctionName: autoLot.autoAuction?.name || 'MISSING',
        companyId: autoLot.companyRef,
        modelId: autoLot.modelNameRef,
        dumpVersion,
        viewsCount: viewsCount.viewsCount,
        vin: autoLot.vin || (autoLot.frame ? autoLot.frame + '-' : ''),
      };
    }

    if (savedLot) {
      lot = this.updateAutoLotViewBySavedData(lot, savedLot);
    }

    return lot;
  }

  private updateAutoLotViewBySavedData(
    lot: AutoLotView,
    savedLot?: UserLotQuery['lot'],
  ): AutoLotView {
    try {
      lot = mergeSavedLotToAutoLotView(lot, savedLot);
    } catch (e) {
      console.error(e);
    }

    const savedImages = savedLot?.images || [];
    const savedThumbnails = savedLot?.thumbnails || [];
    const currentImages = lot?.images || [];

    const shouldUseSavedImages = currentImages.length <= savedImages.length;

    lot.productionDate = savedLot.productionDate;
    lot.vin = savedLot.vin || lot?.vin;
    lot.images = shouldUseSavedImages ? savedImages : currentImages;
    lot.thumbnails = shouldUseSavedImages ? savedThumbnails : lot?.thumbnails;
    lot.negotiationsPrice = savedLot.negotiationsPrice;
    lot.currentUserLotNote = savedLot.currentUserLotNote;

    return lot;
  }

  private fetchInspection(): void {
    const inspectionRef$ = this.data$.pipe(
      map((data) => this.auctionVideoWatchService.fetchInspection(data)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.inspectionLoading$ = inspectionRef$.pipe(switchMap(({ loading$ }) => loading$));
    this.inspectionData$ = inspectionRef$.pipe(switchMap(({ inspection$ }) => inspection$));
  }

  private fetchAuctionData(): void {
    this.auctionData$ = this.data$.pipe(
      map((lotData) => ({
        lot: lotData?.lot,
        auctionName: lotData?.lot?.auctionName,
      })),
      filter(
        (data): data is { lot: AutoLotView; auctionName: string } =>
          !!data.lot && !!data.auctionName,
      ),
      switchMap(({ lot, auctionName }) =>
        this.auctionService.getAliasAuctionByName(auctionName).pipe(
          map((auction) => ({
            aiTranslationEnabled: !!auction?.auctionDescription?.aiTranslationEnabled,
            currentTradingTimeShift: pickCurrentTradingTimeShift(
              auction?.currentTradingTimeShifts,
              lot,
            ),
          })),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  goToFirstSlide(isUsingSavedImages: boolean): void {
    if (!isUsingSavedImages) {
      this.activeSlide = 1;
    }
  }

  scrollToTranslations(): void {
    document.getElementById('lotTranslations').scrollIntoView({ behavior: 'smooth' });
  }

  scrollToPreviousSales(): void {
    document.getElementById('previousSales').scrollIntoView({ behavior: 'smooth' });
    this.statisticsOpen = true;
  }

  showGuideLotIcons(): void {
    this.isGuideLotIconsVisible = true;
    this.cdr.detectChanges();
  }

  hideGuideLotIcons(): void {
    this.isGuideLotIconsVisible = false;
    this.cdr.detectChanges();
  }

  startGuide(): void {
    this.autoLotGuideService.start({
      showGuideLotIcons: () => this.showGuideLotIcons(),
      hideGuideLotIcons: () => this.hideGuideLotIcons(),
    });
  }

  ngOnDestroy(): void {
    this.disableMediaSavingService.suspend();
    this.autoLotGuideService.forceClose({
      hideGuideLotIcons: () => this.hideGuideLotIcons(),
    });
  }
}
